import { yupResolver } from '@hookform/resolvers/yup'
import {
  AggregatorDetailsForm,
  ApiConfigurationForm,
  FtpConfigurationForm,
  OrderConfiguratinForm,
} from 'components/aggregatorManagement/forms'
import {
  aggregatorFormFields,
  authTypes,
} from 'components/aggregatorManagement/utils'
import BackButton from 'components/backButton'
import { Button, Spinner } from 'components/common'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { getAggregator, updateAggregator } from 'utils/api/aggregatorApi'
import { PAGE_AGGREGATOR_MANAGEMENT, PAGE_NOT_FOUND } from 'utils/constants'
import { formatJson } from 'utils/helpers'

import {
  CreateAggregatorType,
  createAggregatorSchema,
} from 'utils/schema/aggregatorSchema'

const { FTP_ACTIVE, API_ACTIVE, API_AUTH_TYPE } = aggregatorFormFields

const UpdateAggregator = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState<boolean>(true)
  const [aggregator, setAggregator] = useState<any>({})

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<CreateAggregatorType>({
    resolver: yupResolver(createAggregatorSchema),
    defaultValues: aggregator,
  })

  const ftpIsActive = watch(
    FTP_ACTIVE.parentKey + '.' + FTP_ACTIVE.key,
  ) as boolean

  const apiIsActive = watch(
    API_ACTIVE.parentKey + '.' + API_ACTIVE.key,
  ) as boolean

  const currentAuthMode = watch(
    API_AUTH_TYPE.parentKey + '.' + API_AUTH_TYPE.key,
  ) as string

  const getAggregatorDetails = async (id: string) => {
    try {
      const { data } = await getAggregator(id)

      data.aggregatorId = data._id

      if (data?.api?.rootSchema) {
        data.api.rootSchema = formatJson(data.api.rootSchema).value
      }

      if (data?.api?.productSchema) {
        data.api.productSchema = formatJson(data.api.productSchema).value
      }

      if (data?.api?.authRequestSchema) {
        data.api.authRequestSchema = formatJson(
          data.api.authRequestSchema,
        ).value
      }

      setAggregator(data)
      delete data._id
      reset(data)
      setValue(
        API_AUTH_TYPE.parentKey + '.' + API_AUTH_TYPE.key,
        data?.api?.authType || authTypes.BEARER,
      )
    } catch (error) {
      setAggregator({})
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      getAggregatorDetails(id)
    }
  }, [])

  if (loading) {
    return <Spinner />
  }

  const handleUpdateAggregator = async (data: CreateAggregatorType) => {
    if (!id) return

    const toastAggregator = toast.loading('Updating aggregator...', {
      duration: Infinity,
    })

    try {
      delete data.aggregatorId

      await updateAggregator(id, data)

      toast.success('Aggregator updated successfully!', {
        id: toastAggregator,
        duration: 3000,
      })

      reset()
      navigate(PAGE_AGGREGATOR_MANAGEMENT.path)
    } catch (error) {
      toast.error('Failed to update aggregator!', {
        id: toastAggregator,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>ORMS Update Aggregator</title>
      </Helmet>

      <BackButton />

      <form onSubmit={handleSubmit(handleUpdateAggregator)}>
        {/* Aggregator Details */}
        <AggregatorDetailsForm
          register={register}
          errors={errors}
          setValue={setValue}
          isUpdate
          aggregator={aggregator}
        />

        {/* Aggregator FTP Details */}
        <FtpConfigurationForm
          register={register}
          errors={errors}
          setValue={setValue}
          aggregator={aggregator}
          ftpIsActive={ftpIsActive}
        />

        {/* Aggregator API Details */}
        <ApiConfigurationForm
          register={register}
          errors={errors}
          setValue={setValue}
          aggregator={aggregator}
          apiIsActive={apiIsActive}
          currentAuthMode={currentAuthMode}
          control={control}
        />
        <OrderConfiguratinForm
          register={register}
          errors={errors}
          setValue={setValue}
          aggregator={aggregator}
          isEditPage
        />

        <Button buttonText="Update" className="mt-7 w-44" />
      </form>
    </>
  )
}

export default UpdateAggregator
